import {LngLat, LngLatBounds} from "maplibre-gl";
import {Geolocation} from "@capacitor/geolocation";
import {Capacitor} from "@capacitor/core";
import {Position} from "@capacitor/geolocation/dist/esm/definitions";
import {IAddressRequest, ILocationAddress, ILocationAddresses, IPendingAddressRequest} from "./LocationInterface";
import DeferredPromise, {createDeferredPromise} from "../../../ts/common/DeferredPromise";
import {dispatchLocationUpdate} from "../../../ts/event/DispatchEvent";
import ApiError from "../../../ts/manager/api/ApiError";
import {degreesToRadians, formatNumber, isArray, isNumeric, isObject, keySort} from "../../../ts/common/SiteHelper";
import {CountryCode} from "./LocationType";
import {IAnyObject} from "../../../ts/common/SimpleInterface";
import {getManager} from "../../../ts/manager/Manager";
import {CountryLocations} from "./LocationConfig";

type PermissionStatus =  'tbd' | 'in_progress' | 'prompt' | 'granted' | 'denied' | 'error';

export default class LocationManager
{
    lastKnownUserPosition: Position|null = null;

    lastKnownUserPositionRetrievedAt: number = 0;
    expireRecentLocationAfter: number = 1000 * 60 * 10;
    lastKnownUserAddress: ILocationAddress|null = null;
    lastAddressErrorMessage: string|null = null;
    searchResults:ILocationAddresses = {};
    pendingRequests:IPendingAddressRequest[] = [];
    isRequestInProgress:boolean = false;
    permissionStatus: PermissionStatus = 'tbd';
    onUserLocationUpdatePromises:DeferredPromise<void, void>[] = [];
    didAllowPrompt:boolean = false;
    determiningAddress:boolean = false;
    googleAddressEndpoint:string = "";

    constructor()
    {

    }

    isUserLocationDetermined(withPrompt:boolean): boolean
    {
        if(this.determiningAddress){
            return false
        }

        if(this.permissionStatus === 'granted'){
            return true;
        }
        const notDetermined:PermissionStatus[] = ['tbd', 'in_progress', 'prompt'];
        if(notDetermined.includes(this.permissionStatus)){
            return false;
        }
        return this.didAllowPrompt || !withPrompt
    }

    isDeterminingPermissionStatus(): boolean
    {
        const determining:PermissionStatus[] = ['in_progress', 'prompt'];
        return determining.includes(this.permissionStatus);
    }

    async requireUserLocation(allowPrompt:boolean, recentLocation:boolean = false):Promise<void>
    {
        let isRefresh = false;
        if(this.permissionStatus === 'granted'){
            if(recentLocation && (Date.now() - this.lastKnownUserPositionRetrievedAt > this.expireRecentLocationAfter)){
                isRefresh = true;
            }
        }

        if(!isRefresh && this.isUserLocationDetermined(allowPrompt)){
            return;
        }
        const promise:DeferredPromise<void, void> = createDeferredPromise<void, void>();

        this.onUserLocationUpdatePromises.push(promise)

        if(!isRefresh && this.permissionStatus !== 'tbd' && !(allowPrompt && !this.didAllowPrompt)){
            return promise
        }

        return this.determineUserLocation(allowPrompt, isRefresh).then(() => {
            if(!allowPrompt && this.didAllowPrompt){
                return;
            }
            for(let promise of this.onUserLocationUpdatePromises){
                promise.resolve();
            }
            this.onUserLocationUpdatePromises.length = 0;
            dispatchLocationUpdate();
            return promise
        })
    }

    async determineUserLocation(allowPrompt:boolean, isRefresh:boolean = false): Promise<void>
    {
        if(allowPrompt){
            this.didAllowPrompt = true;
        }
        const getLocation = (): Promise<void> => {
            this.determiningAddress = true;
            return new Promise(async (resolve) =>
            {

                const position = await this.getCurrentPosition();
                if (!position)
                {
                    this.determiningAddress = false;
                    resolve();
                    return;
                }
                this.lastKnownUserPosition = position;
                this.lastKnownUserPositionRetrievedAt = Date.now();
                this.lastKnownUserAddress = null;
                return this.reverseRequestAddress(position.coords.latitude, position.coords.longitude).then((address) => {
                    this.determiningAddress = false;
                    this.lastKnownUserAddress = address;
                    resolve();
                }).catch((error:ApiError) => {
                    this.determiningAddress = false;
                    this.lastAddressErrorMessage = error.message;
                    resolve();
                });
            })
        }

        this.permissionStatus = 'in_progress';
        this.lastAddressErrorMessage = null;
        if(isRefresh){
            return getLocation();
        }
        return this.checkPermissions().then((status) =>{
            if(status === "granted"){
                return getLocation();
            } else if(status === "prompt"){

                if(allowPrompt){

                    if(Capacitor.isNativePlatform()){
                        return this.requestPermissions().then(status => {
                            if(status === "granted"){
                                return getLocation();
                            }
                        })
                    } else {
                        return getLocation();
                    }
                } else {
                    this.permissionStatus = 'tbd';
                }

            }
        })
    }

    async checkPermissions(): Promise<PermissionStatus>
    {
        return Geolocation.checkPermissions().then(permissions => {
            if(permissions.location === 'granted'){
                this.permissionStatus = 'granted'
            } else if(permissions.location === 'denied'){
                this.permissionStatus = 'denied'
            } else {
                this.permissionStatus = 'prompt'
            }
            return this.permissionStatus;
        }).catch(positionError => {
            this.handlePositionError(positionError);
            return this.permissionStatus;
        })
    }

    async requestPermissions(): Promise<PermissionStatus>
    {
        if(Capacitor.isNativePlatform()){
            return Geolocation.requestPermissions().then(permissions => {
                if(permissions.location === 'granted'){
                    this.permissionStatus = 'granted'
                } else if(permissions.location === 'denied'){
                    this.permissionStatus = 'denied'
                } else {
                    this.permissionStatus = 'error'
                }
                return this.permissionStatus;
            }).catch(positionError => {
                this.handlePositionError(positionError);
                return this.permissionStatus;
            })
        } else {
            return this.getCurrentPosition().then(() => {
                return this.permissionStatus;
            })
        }
    }

    async getCurrentPosition(): Promise<Position|null>
    {
        return Geolocation.getCurrentPosition({enableHighAccuracy: true}).then((position:Position) => {
            this.permissionStatus = 'granted'
            return position;
        }).catch(positionError => {
            this.handlePositionError(positionError);
            return null;
        })
    }

    handlePositionError(positionError?:any):void
    {
        const code = positionError && isObject(positionError) ? positionError.code : null;
        if(!isNaN(code) && parseInt(code) === 1){
            this.permissionStatus = 'denied'
        } else {
            this.permissionStatus = 'error'
        }
    }

    getPermissionErrorMessage():string | null
    {
        if(this.isGeolocationDeniedByUser())
        {
            return "Please enable geolocation in your settings."
        }
        if(this.isGeolocationUnavailable())
        {
            return "Geolocation is not currently available. Please check your settings and try again."
        }
        return null;
    }

    isGeolocationUnavailable()
    {
        return this.permissionStatus === 'error' || this.permissionStatus === 'denied';
    }

    isGeolocationDeniedByUser()
    {
        return this.permissionStatus === 'denied';
    }

    getLastKnowLngLat(): LngLat|null
    {
        if(!this.lastKnownUserPosition){
            return null;
        }
        return new LngLat(this.lastKnownUserPosition.coords.longitude, this.lastKnownUserPosition.coords.latitude);
    }

    async getAddressWithTownOrPostcode(townOrPostcode:string, addressRequest?:IAddressRequest, countryCodes?:string[])
    {
        if(!townOrPostcode){
            return [];
        }
        addressRequest = addressRequest ? {...addressRequest} : {};
        countryCodes = countryCodes && countryCodes.length ? countryCodes : (addressRequest.country ? [addressRequest.country] : ["gb", "ie"]);
        delete addressRequest.city;
        delete addressRequest.postalcode;
        delete addressRequest.street;
        delete addressRequest.q;

        addressRequest.city = townOrPostcode;

        for(let countryCode of countryCodes){
            addressRequest.country = countryCode;
            const address = await this.getAddress(addressRequest);
            if(address.length){
                return address;
            }
        }
        delete addressRequest.city;
        addressRequest.postalcode = townOrPostcode;
        for(let countryCode of countryCodes){
            addressRequest.country = countryCode;
            const address = await this.getAddress(addressRequest);
            if(address.length){
                return address;
            }
        }
        delete addressRequest.postalcode;
        const parts = townOrPostcode.split(" ");
        const q = (parts.length > 1 ? parts[0] : "").toUpperCase();
        if(q.startsWith("BT") && (q.length === 3 || q.length === 4)){
            addressRequest.q = q;
            const address = await this.getAddress(addressRequest);
            if(address.length){
                return address;
            }
        }
        return [];
    }

    async getAddress(addressRequest:IAddressRequest): Promise<ILocationAddress[]>
    {
        const resultKey = JSON.stringify(keySort(JSON.parse(JSON.stringify(addressRequest))));
        const promise:DeferredPromise<ILocationAddress[], ApiError> = createDeferredPromise<ILocationAddress[], ApiError>();

        const pendingRequest:IPendingAddressRequest = {
            resultKey: resultKey,
            addressRequest: addressRequest,
            promise: promise
        }

        this.pendingRequests.push(pendingRequest);
        this.handleNextAddress();
        return promise;
    }

    async getCountryLocation(countryCode:CountryCode): Promise<ILocationAddress|null>
    {
        const request:IAddressRequest = {
            country: countryCode
        }
        return this.getAddress(request).then((itemAddresses:ILocationAddress[]) => {
            return itemAddresses[0] ?? null;
        }).catch((() => {
            return null
        }));
    }

    handleNextAddress(): void
    {
        let nextRequest:IPendingAddressRequest|null = null;
        for(let i = 0; i < this.pendingRequests.length; i++)
        {
            const pendingRequest = this.pendingRequests[i];
            const searchResult:ILocationAddress[]|null = this.searchResults[pendingRequest.resultKey] ?? null;
            if(searchResult !== null)
            {
                pendingRequest.promise.resolve(searchResult);
                this.pendingRequests.splice(i, 1);
                i --;
                continue;
            }
            if(!nextRequest){
                nextRequest = pendingRequest;
            }
        }
        if(!nextRequest || this.isRequestInProgress){
            return;
        }
        if(nextRequest.addressRequest.country && !nextRequest.addressRequest.city){

            const countryCode = nextRequest.addressRequest.country.toLowerCase() as CountryCode;
            const lngLat = this.getCountryConfigLngLat(countryCode)
            const lngLatBounds = this.getCountryConfigLngLatBounds(countryCode)
            if(lngLat && lngLatBounds)
            {
                this.searchResults[nextRequest.resultKey] = [{
                    id: 0,
                    displayName: countryCode,
                    lngLat: lngLat,
                    lngLatBounds:lngLatBounds,
                    country: countryCode,
                    countryCode:countryCode,
                    region: "",
                    county: "",
                    town: "",
                    street: "",
                    postcode: "",
                    placeID: null,
                    zoom: null,
                    specific: {

                    }
                }];
                this.isRequestInProgress = false;
                this.handleNextAddress();
                return;
            }
        }
        this.isRequestInProgress = true;
        this.requestAddresses(nextRequest.addressRequest).then((searchResult:ILocationAddress[]) => {
            this.searchResults[nextRequest.resultKey] = searchResult;
            this.isRequestInProgress = false;
            this.handleNextAddress();
        }).catch((error:ApiError) => {
            nextRequest.promise.reject(error);

            for(let i = 0; i < this.pendingRequests.length; i++)
            {
                if(this.pendingRequests[i] === nextRequest){
                    this.pendingRequests.splice(i, 1);
                    break;
                }
            }
            this.isRequestInProgress = false;
            this.handleNextAddress();
        })
    }

    async requestAddresses(addressRequest:IAddressRequest): Promise<ILocationAddress[]>
    {
        if(!addressRequest.googleRequest){

            const params:IAnyObject = {
                format: "jsonv2",
                addressdetails: "1"
            }
            if(addressRequest.q){
                params.q = addressRequest.q;
            } else {
                for(let key in addressRequest){
                    params[key] = addressRequest[key];
                }
            }

            return getManager().api.fetchData("https://nominatim.openstreetmap.org/search", "GET", params).then(responseVO => {

                if(responseVO.apiError){
                    return Promise.reject(responseVO.apiError);
                }
                const values = responseVO.response?.values ?? [];
                const addresses:ILocationAddress[] = [];
                for(let i = 0 ; i < values.length ; i++){
                    const fromData = isObject(values[i]) ? values[i] : {};
                    const address = this.parseAddressResponse(fromData);
                    if(address){
                        addresses.push(address)
                    }
                }
                return addresses;

            });
        } else {
            if(!this.googleAddressEndpoint){
                return Promise.reject(new ApiError(0, "", "No Google endpoint"));
            }
            const params:IAnyObject = {}
            for(let key in addressRequest.googleRequest){

                params[key] = addressRequest.googleRequest[key];
            }
            return getManager().api.fetchData(this.googleAddressEndpoint, "GET", params, true).then(responseVO => {

                if(responseVO.apiError){
                    return Promise.reject(responseVO.apiError);
                }
                const addresses:ILocationAddress[] = [];
                const values = responseVO.response?.values ?? {};
                const address = isObject(values)  ? this.parseAddressResponse(values) : null;
                if(!address){
                    return Promise.reject(new ApiError(0, "", "Invalid address"));
                }
                return addresses;

            });
        }
    }

    async reverseRequestAddress(latitude:number, longitude:number): Promise<ILocationAddress>
    {
        const params:IAnyObject = {
            format: "jsonv2",
            addressdetails: "1",
            lat: latitude,
            lon: longitude
        }
        return getManager().api.fetchData("https://nominatim.openstreetmap.org/reverse", "GET", params).then(responseVO => {
            if(responseVO.apiError){
                return Promise.reject(responseVO.apiError);
            }
            const address = this.parseAddressResponse(responseVO.data);
            if(!address){
                return Promise.reject(new ApiError(404, "Address not found"));
            }
            return address;
        });
    }


    parseGooglResponse(fromData:IAnyObject):ILocationAddress|null
    {
        const latitude = isNumeric(fromData.lat) ? parseFloat(fromData.lat) : 0;
        const longitude = isNumeric(fromData.lon) ? parseFloat(fromData.lon) : 0;

        if(!(latitude || longitude)){
            return null;
        }
        return {
            id: parseInt(fromData.place_id ?? 0),
            displayName: "",
            lngLat: new LngLat(longitude, latitude),
            lngLatBounds: null,
            country:"",
            countryCode: "",
            region: "",
            county: "",
            town: "",
            street: fromData.address ?? (fromData.address ?? ""),
            postcode: "",
            placeID: null,
            zoom: null,
            specific: {}
        }
    }

    parseAddressResponse(fromData:IAnyObject):ILocationAddress|null
    {
        const displayName:string = fromData.display_name ?? "";
        const latitude = isNumeric(fromData.lat) ? parseFloat(fromData.lat) : 0;
        const longitude = isNumeric(fromData.lon) ? parseFloat(fromData.lon) : 0;
        const fromAddress:IAnyObject = isObject(fromData.address) ? fromData.address : {};
        const boundingBox:string[] = isArray(fromData.boundingbox) ? fromData.boundingbox : [];
        let lngLatBounds:LngLatBounds|null = null;
        if(boundingBox.length >= 4){
            const swLatitude = isNumeric(boundingBox[0]) ? parseFloat(boundingBox[0]) : 0;
            const swLongitude = isNumeric(boundingBox[2]) ? parseFloat(boundingBox[2]) : 0;
            const neLatitude = isNumeric(boundingBox[1]) ? parseFloat(boundingBox[1]) : 0;
            const neLongitude = isNumeric(boundingBox[3]) ? parseFloat(boundingBox[3]) : 0;
            if(swLatitude || swLongitude || neLatitude || neLongitude){
                lngLatBounds = new LngLatBounds(new LngLat(swLongitude, swLatitude), new LngLat(neLongitude, neLatitude));
            }
        }
        if(!displayName || !(latitude || longitude) || !Object.keys(fromAddress).length || !lngLatBounds){
            return null;
        }


        const address:ILocationAddress = {
            id: parseInt(fromData.place_id ?? 0),
            displayName: displayName,
            lngLat: new LngLat(longitude, latitude),
            lngLatBounds: lngLatBounds,
            country: fromAddress.country ?? (fromAddress.country_code  ?? ""),
            countryCode: (fromAddress.country_code ?? "").toLowerCase(),
            region: fromAddress.state ?? (fromAddress.region ?? ""),
            county: fromAddress.county ?? (fromAddress.state_district ?? (fromAddress.district ?? (fromAddress.municipality ?? ""))),
            town: fromAddress.city ?? (fromAddress.town ?? (fromAddress.village ?? (fromAddress.borough ?? (fromAddress.hamlet ?? (fromAddress.suburb ?? ""))))),
            street: fromAddress.road ?? (fromAddress.street ?? ""),
            postcode: fromAddress.postcode ?? "",
            placeID: fromAddress.place_id ? parseInt(fromAddress.place_id) : null,
            zoom: null,
            specific: {}
        }
        if(fromAddress.state){
            address.specific.state = fromAddress.state;
        }
        if(fromAddress.region){
            address.specific.region = fromAddress.region;
        }
        if(fromAddress.county){
            address.specific.county = fromAddress.county;
        }
        if(fromAddress.state_district){
            address.specific.stateDistrict = fromAddress.state_district;
        }
        if(fromAddress.district){
            address.specific.district = fromAddress.district;
        }
        if(fromAddress.municipality){
            address.specific.municipality = fromAddress.municipality;
        }
        if(fromAddress.city){
            address.specific.city = fromAddress.city;
        }
        if(fromAddress.town){
            address.specific.town = fromAddress.town;
        }
        if(fromAddress.village){
            address.specific.village = fromAddress.village;
        }
        if(fromAddress.borough){
            address.specific.borough = fromAddress.borough;
        }
        if(fromAddress.hamlet){
            address.specific.hamlet = fromAddress.hamlet;
        }
        if(fromAddress.suburb){
            address.specific.suburb = fromAddress.suburb;
        }
        return address;
    }

    getCountryConfigLngLat(countryCode:CountryCode):LngLat|null
    {
        const countryLocationConfig = CountryLocations[countryCode] ?? null;
        if(!countryLocationConfig){
            return null;
        }
        if(
            ((countryLocationConfig.lat ?? null) === null) ||
            ((countryLocationConfig.lng ?? null) === null)
        )
        {
            return null;
        }
        return new LngLat(countryLocationConfig.lng, countryLocationConfig.lat)
    }

    getCountryConfigLngLatBounds(countryCode:CountryCode):LngLatBounds|null
    {
        const countryLocationConfig = CountryLocations[countryCode] ?? null;
        if(!countryLocationConfig){
            return null;
        }
        return this.determineBounds(countryLocationConfig.lat, countryLocationConfig.lng, countryLocationConfig.width, countryLocationConfig.height)
    }

    getDistanceInMetresBetween(lngLat1:LngLat, lngLat2:LngLat):number
    {
        const lat1 = degreesToRadians(lngLat1.lat);
        const lng1 = degreesToRadians(lngLat1.lng);
        const lat2 = degreesToRadians(lngLat2.lat);
        const lng2 = degreesToRadians(lngLat2.lng);
        if((lat1 === lat2) && (lng1 === lng2)){
            return 0;
        }

        return Math.round((6371 * 1000 * Math.acos( Math.cos( lat2 ) * Math.cos( lat1 ) * Math.cos( lng1 - lng2 ) + Math.sin( lat2 ) * Math.sin( lat1 ) )));
    }

    getBoundsWidth(bounds:LngLatBounds)
    {
        const east = bounds.getEast();
        const west = bounds.getWest();
        return east - west;
    }

    getBoundsHeight(bounds:LngLatBounds)
    {
        const north = bounds.getNorth();
        const south = bounds.getSouth();
        return north - south;
    }

    determineBounds(centreLat:number, centreLng:number, width:number, height:number):LngLatBounds
    {
        const north = centreLat + height / 2;
        const south = centreLat - height / 2;
        const west = centreLng - width / 2;
        const east = centreLng + width / 2;

        return new LngLatBounds(new LngLat(west, south), new LngLat(east, north));
    }

    getReadableDistance(distanceInMetres:number):string
    {
        let metric;
        let value;
        if(distanceInMetres < 1000)
        {
            metric = "m";
            value = distanceInMetres;
        } else {
            metric = "km";
            if(distanceInMetres < 10000)
            {
                value = Math.round(distanceInMetres / 100) / 10;
            } else {
                value = Math.round(distanceInMetres / 1000);
            }
        }
        return formatNumber(value)+metric;
    }
}